/**
 * Autopilot — the shark swims itself when you let go.
 *
 * The whole design is one decision: this produces CONTROLS, not motion. It returns the same
 * {steer, throttle, dive, rise} the keyboard produces and hands them to the same physics, so
 * an autopiloting shark has identical momentum, identical turn-rate falloff, identical bank
 * and body-bend. Nothing about it is a separate movement model, which means there is no
 * moment of transition to get wrong — taking over is just the keyboard winning the argument
 * about who fills the struct.
 *
 * WHAT IT WANTS
 *   excursion   a cross-shore destination, re-rolled every half-minute or so, weighted toward
 *               the reef. This is what makes an idle shark tour the coast rather than circle.
 *   patrol      a heading along the shore, held for a long time, because the coast is endless
 *               and the point of it is to be travelled.
 *   avoid       depth-driven, not Z-driven: the shoreline meanders, so the shark has to feel
 *               the bottom coming up as it rounds a headland.
 *   wander      two incommensurate sines, so the drift never repeats and never looks periodic.
 *   tempo       and it does not hold one speed. A shark loafs, cruises, and now and then
 *               puts in a short burst — constant velocity is the fastest way to make an
 *               animal read as a vehicle.
 */
import type { Input } from '../core/Input.ts';
import { Rng } from '../core/Rng.ts';
import { DEPTH, MOVEMENT, OCEAN } from '../data/tuning.ts';
import { bedHeight, waterDepth, lagoonEdge, COAST } from '../world/Coast.ts';

/** the control surface the physics reads; the keyboard and this fill the same shape */
export interface Controls {
  /** -1..1 rudder. Positive is a right rudder, which DECREASES heading. */
  steer: number;
  /** -1..1 forward thrust */
  throttle: number;
  /**
   * -1..1 commanded climb rate, as a fraction of DEPTH.riseSpeed / diveSpeed.
   *
   * ANALOG, unlike the keyboard's on/off dive and rise. A boolean autopilot has to run a dead
   * band wide enough to stop it hunting at full dive rate — and that band then swallows the
   * very depth changes it was choosing. Measured before this change: it asserted dive or rise
   * on 2.4% of frames and sat at the passive cruise depth the rest of the time. A rate that
   * goes to zero AT the target has nothing to hunt, so no band is needed at all.
   */
  climb: number;
}

/** hands-off time before the shark starts swimming itself, s */
const IDLE_DELAY = 2.5;
/** how long the autopilot takes to come up to full authority, s */
const BLEND_IN = 1.4;
/** radians of heading error per unit of rudder */
const STEER_GAIN = 1.9;

/**
 * Swimming tempo: a throttle scale, a duration, and how often it comes up.
 *
 * Weighted so the animal spends most of its time cruising or loafing and only occasionally
 * puts in a burst — a shark that sprinted a third of the time would read as agitated. The
 * durations matter as much as the speeds: a spurt is SHORT, and the long slow stretches are
 * what make it land when it happens.
 */
const TEMPOS: Array<{ scale: [number, number]; hold: [number, number]; weight: number }> = [
  { scale: [0.09, 0.22], hold: [7, 18], weight: 1.1 },    // loafing,  ~3-5 u/s
  { scale: [0.33, 0.56], hold: [9, 24], weight: 1.8 },    // cruising, ~6-8 u/s
  { scale: [0.86, 1.00], hold: [2.5, 6.0], weight: 0.7 }, // a burst,  ~10-11 u/s
];

/* Those scales are throttle, not speed, and the two are NOT proportional: drag here is
   linear plus quadratic, so thrust ~ 0.55v + 0.23v^2 and the top of the range is far more
   crowded than the bottom. A tempo table spread evenly in throttle lands 64% of its time in
   one narrow speed band. The numbers above are chosen against that curve. */

/** cross-shore destinations, and how often the shark chooses each */
const EXCURSIONS: Array<[[number, number], number]> = [
  [[58, 102], 1.6],   // the reef — where the interesting water is
  [[20, 50], 1.0],    // the lagoon
  [[104, 128], 0.7],  // the fore-reef and the lip of the drop-off
];

export class Autopilot {
  /** true while the autopilot is driving; the HUD reads this */
  engaged = false;

  private readonly rng: Rng;
  private readonly out: Controls = { steer: 0, throttle: 0, climb: 0 };
  private idle = 0;
  /** 0 manual, 1 full authority — ramps in, but is dropped to 0 the instant you touch a key */
  private blend = 0;
  private targetZ = 80;
  private nextRoll = 0;
  private dir = 1;
  private nextFlip = 0;
  private columnAt = 0.35;
  /** commanded throttle scale, and the eased value actually flown */
  private tempo = 0.6;
  private tempoLive = 0.6;
  private tempoUntil = 0;
  private t = 0;
  private readonly wanderA: number;
  private readonly wanderB: number;

  constructor(seed = 0xa17b0a7) {
    this.rng = new Rng(seed >>> 0);
    this.wanderA = this.rng.range(0.055, 0.10);
    this.wanderB = this.rng.range(0.17, 0.29);
    this.roll();
    this.nextFlip = this.rng.range(25, 70);
    this.rollTempo(0);
  }

  private roll(): void {
    let total = 0;
    for (const e of EXCURSIONS) total += e[1];
    let r = this.rng.next() * total;
    let band = EXCURSIONS[0][0];
    for (const [b, w] of EXCURSIONS) {
      r -= w;
      if (r <= 0) { band = b; break; }
    }
    this.targetZ = this.rng.range(band[0], band[1]);
    /* Where in the water column to ride. Spread wide on purpose: 0.12 is belly-to-the-sand
       hunting the reef, 0.78 is high enough that the dorsal is near the light. A narrow range
       centred on the passive cruise depth is a choice that makes no visible difference. */
    this.columnAt = this.rng.range(0.12, 0.78);
    this.nextRoll = this.t + this.rng.range(18, 44);
  }

  /**
   * Choose the next swimming tempo.
   *
   * @param steer the rudder currently being flown. A burst is refused mid-turn: turn
   *              authority falls off with speed in this movement model, so accelerating into
   *              a carve just makes the animal swing wide, and a real shark opens up on the
   *              straight anyway.
   */
  private rollTempo(steer: number): void {
    let total = 0;
    for (const t of TEMPOS) total += t.weight;
    let r = this.rng.next() * total;
    let pick = TEMPOS[1];
    for (const t of TEMPOS) {
      r -= t.weight;
      if (r <= 0) { pick = t; break; }
    }
    if (pick.scale[0] > 0.8 && Math.abs(steer) > 0.35) pick = TEMPOS[1];
    this.tempo = this.rng.range(pick.scale[0], pick.scale[1]);
    this.tempoUntil = this.t + this.rng.range(pick.hold[0], pick.hold[1]);
  }

  /** true if the player is asking for anything at all this tick */
  private static handsOn(input: Input): boolean {
    return (
      Math.abs(input.steer) > 1e-3 ||
      Math.abs(input.throttle) > 1e-3 ||
      input.isDown('dive') ||
      input.isDown('rise')
    );
  }

  /**
   * @returns the controls to fly, or null to leave the player in charge.
   */
  update(dt: number, input: Input, x: number, z: number, y: number, heading: number, speed: number): Controls | null {
    this.t += dt;

    if (Autopilot.handsOn(input)) {
      // Instant, not a fade. "Can be taken over at any time" means the first frame you touch
      // a key you have the shark — an autopilot that argues for half a second on the way out
      // feels like input lag, and input lag is the one thing that cannot be forgiven here.
      this.idle = 0;
      this.blend = 0;
      this.engaged = false;
      return null;
    }

    this.idle += dt;
    if (this.idle < IDLE_DELAY) {
      this.engaged = false;
      return null;
    }
    this.engaged = true;
    this.blend = Math.min(1, this.blend + dt / BLEND_IN);

    if (this.t > this.nextRoll) this.roll();
    if (this.t > this.nextFlip) {
      this.dir = -this.dir;
      this.nextFlip = this.t + this.rng.range(25, 70);
    }

    /* --- where it wants to go ------------------------------------------------------------
       The bay has ends, so the patrol direction is overridden near a headland rather than
       waiting for the shore push to stop the shark dead against it. Turning early is the
       difference between an animal that knows the bay and one that keeps walking into it. */
    const nearEnd = lagoonEdge(x);
    if (nearEnd > 0.5 && Math.sign(x) === Math.sign(this.dir)) {
      this.dir = -this.dir;
      this.nextFlip = this.t + this.rng.range(25, 70);
    }
    let wantX = this.dir;
    let wantZ = Math.max(-1.5, Math.min(1.5, (this.targetZ - z) * 0.055));

    /* Avoidance overrides the excursion rather than adding to it, and it reads DEPTH, not Z:
       the shoreline meanders by ±26 u, so a fixed cross-shore limit would let the shark swim
       confidently onto a headland it never saw coming. */
    const depth = waterDepth(x, z);
    const margin = COAST.minSwimDepth + 3.5;
    if (depth < margin) wantZ += (margin - depth) * 0.85;
    const edge = COAST.edgeZ - 10;
    if (z > edge) wantZ -= (z - edge) * 0.32;

    // --- wander --------------------------------------------------------------------------
    const w =
      Math.sin(this.t * this.wanderA * Math.PI * 2) * 0.7 +
      Math.sin(this.t * this.wanderB * Math.PI * 2 + 1.7) * 0.3;

    const desired = Math.atan2(wantX, wantZ) + w * 0.40;

    // --- rudder --------------------------------------------------------------------------
    let delta = desired - heading;
    while (delta > Math.PI) delta -= Math.PI * 2;
    while (delta < -Math.PI) delta += Math.PI * 2;
    // Negated: the rig's heading grows from +Z toward +X, so a right rudder DECREASES it.
    const steer = Math.max(-1, Math.min(1, -delta * STEER_GAIN));

    /* Tempo. Eased rather than stepped, so a burst is a build and a fade — a throttle that
       jumps reads as a vehicle changing gear. Faster to spool up than to settle back down,
       because that is the asymmetry a tail actually has. */
    if (this.t > this.tempoUntil) this.rollTempo(steer);
    const rate = this.tempo > this.tempoLive ? 0.9 : 0.5;
    this.tempoLive += (this.tempo - this.tempoLive) * (1 - Math.exp(-rate * dt));

    /* Ease off the throttle in a hard turn. Not a cosmetic touch — turn authority falls off
       with speed in this movement model, so a shark that holds full thrust through a turn
       fights its own rudder and swings wide every time. */
    const throttle = this.tempoLive * (1 - 0.42 * Math.abs(steer));

    // --- depth ---------------------------------------------------------------------------
    // The physics already levels toward its own cruise depth with no input at all, so dive
    // and rise are only asserted when the shark is meaningfully off the slot it wants.
    const floor = bedHeight(x, z) + DEPTH.floorClearance;
    const room = OCEAN.surfaceHeight - DEPTH.breachSpan - floor;
    const wantY = floor + Math.max(0, room) * this.columnAt;
    // Proportional, saturating about 3 u out: close in it eases onto the slot, far out it
    // commits to a real climb or dive.
    const climb = Math.max(-1, Math.min(1, (wantY - y) * 0.30));

    this.out.steer = steer * this.blend;
    this.out.throttle = Math.min(throttle, speed / MOVEMENT.cruiseSpeed + 0.35) * this.blend;
    this.out.climb = climb * this.blend;
    return this.out;
  }

  /** for the debug overlay */
  status(): string {
    if (!this.engaged) return `MANUAL (${this.idle.toFixed(1)}s)`;
    const gait = this.tempoLive > 0.78 ? 'burst' : this.tempoLive > 0.42 ? 'cruise' : 'loaf';
    return `AUTO ${gait} ${this.tempoLive.toFixed(2)} -> z ${this.targetZ.toFixed(0)}`;
  }
}
